import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useSubscription } from "@/hooks/useSubscription";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Loader2, Briefcase, Building2, ArrowLeft, Menu, X } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { toast } from "sonner";
import { SubscriptionGate } from "@/components/SubscriptionGate";
import { DashboardLayout } from "@/components/DashboardLayout";
import { useJobContext } from "@/contexts/JobContext";

interface JobAnalysis {
  id: string;
  job_title: string;
  company: string | null;
  keywords: string[];
  required_skills: string[];
  nice_to_have_skills: string[];
  experience_level: string | null;
  salary_range: string | null;
  analysis_notes: string | null;
  created_at: string;
}

const JobAnalyzer = () => {
  const navigate = useNavigate();
  const { hasFeature } = useSubscription();
  const { selectedJob, clearSelectedJob } = useJobContext();
  const [jobTitle, setJobTitle] = useState("");
  const [company, setCompany] = useState("");
  const [jobDescription, setJobDescription] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState<JobAnalysis | null>(null);
  const [pastAnalyses, setPastAnalyses] = useState<JobAnalysis[]>([]);

  // Auto-populate from JobContext
  useEffect(() => {
    if (selectedJob) {
      setJobTitle(selectedJob.title);
      setCompany(selectedJob.company);
      setJobDescription(selectedJob.description || '');
      toast.success(`Analyzing: ${selectedJob.title} at ${selectedJob.company}`);
    }
  }, [selectedJob]);

  useEffect(() => {
    fetchPastAnalyses();
    
    // Listen for job data updates from floating panel
    const handleJobDataUpdate = (e: CustomEvent) => {
      const jobData = e.detail;
      setJobTitle(jobData.title);
      setCompany(jobData.company);
      setJobDescription(jobData.description);
      toast.success(`Job loaded: ${jobData.title} at ${jobData.company}`);
    };
    
    window.addEventListener('jobDataUpdated', handleJobDataUpdate as EventListener);
    
    return () => {
      window.removeEventListener('jobDataUpdated', handleJobDataUpdate as EventListener);
    };
  }, []);

  const analyzeJob = async () => {
    if (!jobDescription.trim()) {
      toast.error("Please enter a job description");
      return;
    }

    setAnalyzing(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate("/login");
        return;
      }

      // Generate job hash for idempotency
      const encoder = new TextEncoder();
      const data = encoder.encode(`${jobTitle}:${company}:${jobDescription.substring(0, 200)}`);
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const jobHash = hashArray.map(b => b.toString(16).padStart(2, '0')).join('').slice(0, 16);
      const refId = `jobanalyze:${jobHash}:${Date.now()}`;

      // Phase 1: Charge credits atomically
      const { data: creditResult, error: creditError } = await supabase.functions.invoke('credits-spend', {
        body: { featureKey: 'job_analyzer.analyze', refId }
      });

      if (creditError) {
        const errorData = creditError as any;
        if (errorData.error === 'INSUFFICIENT_CREDITS') {
          toast.error(errorData.message || 'Insufficient credits', {
            action: { label: 'Get Credits', onClick: () => navigate('/pricing') },
          });
          return;
        }
        throw creditError;
      }

      // Phase 2: Execute AI analysis
      const { data: analysisData, error } = await supabase.functions.invoke("analyze-job-description", {
        body: { job_title: jobTitle, company, job_description: jobDescription },
      });

      if (error) throw error;

      setAnalysis(analysisData);
      
      if (!creditResult.idempotent && creditResult.cost > 0) {
        toast.success(`Job analyzed! Used ${creditResult.cost} credits. ${creditResult.remaining} remaining.`);
      } else {
        toast.success("Job analyzed successfully!");
      }
      
      // Refresh past analyses and credits display
      fetchPastAnalyses();
      window.dispatchEvent(new CustomEvent('creditsUpdated'));
    } catch (error) {
      console.error("[JOB-ANALYZER] Error:", error);
      toast.error("Failed to analyze job description");
    } finally {
      setAnalyzing(false);
    }
  };

  const fetchPastAnalyses = async () => {
    try {
      const { data, error } = await supabase
        .from("job_analyses")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(5);

      if (error) throw error;
      setPastAnalyses((data || []) as JobAnalysis[]);
    } catch (error) {
      console.error("Error fetching analyses:", error);
    }
  };

  if (!hasFeature('jobAnalyzer')) {
    return (
      <DashboardLayout>
        <SubscriptionGate
          feature="Job Description Analyzer"
          requiredPlan="premium"
          description="AI-powered analysis of job postings to identify keywords, required skills, and match opportunities to your profile."
        />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Job Description Analyzer</h1>
            <p className="text-muted-foreground">Analyze job postings to understand requirements and optimize your application</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Input Form */}
        <Card className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-semibold">Paste Job Description</h2>
            {selectedJob && (
              <Button variant="ghost" size="sm" onClick={clearSelectedJob}>
                <X className="h-4 w-4 mr-1" />
                Clear Job
              </Button>
            )}
          </div>
          
          {selectedJob && (
            <div className="bg-primary/10 border border-primary/20 rounded-lg p-3 mb-4">
              <p className="text-sm">
                Analyzing: <strong>{selectedJob.title}</strong> at <strong>{selectedJob.company}</strong>
              </p>
            </div>
          )}
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="jobTitle">Job Title</Label>
              <Input
                id="jobTitle"
                placeholder="e.g., Senior Software Engineer"
                value={jobTitle}
                onChange={(e) => setJobTitle(e.target.value)}
              />
            </div>

            <div>
              <Label htmlFor="company">Company (Optional)</Label>
              <Input
                id="company"
                placeholder="e.g., Acme Corp"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
              />
            </div>

            <div>
              <Label htmlFor="jobDesc">Job Description</Label>
              <Textarea
                id="jobDesc"
                placeholder="Paste the full job description here..."
                value={jobDescription}
                onChange={(e) => setJobDescription(e.target.value)}
                rows={12}
                className="resize-none"
              />
            </div>

            <Button
              onClick={analyzeJob}
              disabled={analyzing || !jobDescription.trim()}
              className="w-full"
              size="lg"
            >
              {analyzing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Briefcase className="mr-2 h-4 w-4" />
                  Analyze Job Posting
                </>
              )}
            </Button>
          </div>
        </Card>

        {/* Results */}
        <div className="space-y-6">
          {analysis && (
            <Card className="p-6">
              <h2 className="text-2xl font-semibold mb-4">Analysis Results</h2>
              
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold mb-2 flex items-center gap-2">
                    <Briefcase className="h-4 w-4" />
                    Position Details
                  </h3>
                  <p className="text-lg">{analysis.job_title}</p>
                  {analysis.company && (
                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                      <Building2 className="h-3 w-3" />
                      {analysis.company}
                    </p>
                  )}
                  {analysis.experience_level && (
                    <Badge variant="outline" className="mt-2">{analysis.experience_level}</Badge>
                  )}
                  {analysis.salary_range && (
                    <p className="text-sm mt-2">💰 {analysis.salary_range}</p>
                  )}
                </div>

                {analysis.keywords && analysis.keywords.length > 0 && (
                  <div>
                    <h3 className="font-semibold mb-2">Key Keywords</h3>
                    <div className="flex flex-wrap gap-2">
                      {analysis.keywords.map((keyword, i) => (
                        <Badge key={i} variant="secondary">{keyword}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {analysis.required_skills && analysis.required_skills.length > 0 && (
                  <div>
                    <h3 className="font-semibold mb-2">Required Skills</h3>
                    <div className="flex flex-wrap gap-2">
                      {analysis.required_skills.map((skill, i) => (
                        <Badge key={i} className="bg-red-500">{skill}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {analysis.nice_to_have_skills && analysis.nice_to_have_skills.length > 0 && (
                  <div>
                    <h3 className="font-semibold mb-2">Nice to Have</h3>
                    <div className="flex flex-wrap gap-2">
                      {analysis.nice_to_have_skills.map((skill, i) => (
                        <Badge key={i} variant="outline">{skill}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {analysis.analysis_notes && (
                  <div>
                    <h3 className="font-semibold mb-2">Summary</h3>
                    <p className="text-sm text-muted-foreground">{analysis.analysis_notes}</p>
                  </div>
                )}
              </div>
            </Card>
          )}

          {pastAnalyses.length > 0 && (
            <Card className="p-6">
              <h2 className="text-xl font-semibold mb-4">Recent Analyses</h2>
              <div className="space-y-3">
                {pastAnalyses.map((item) => (
                  <div key={item.id} className="border-b pb-3 last:border-0">
                    <p className="font-medium">{item.job_title}</p>
                    {item.company && (
                      <p className="text-sm text-muted-foreground">{item.company}</p>
                    )}
                    <p className="text-xs text-muted-foreground mt-1">
                      {new Date(item.created_at).toLocaleDateString()}
                    </p>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default JobAnalyzer;
